import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  addPlayableLinkItem, createPlayableLink, deletePlayableLink, deletePlayableLinkItem,
  getPlayableLink, getPlayableLinks, getPlayables, updatePlayableLink, updatePlayableLinkItem,
  type PlayableLink, type PlayableLinkDetail,
} from '@/api/playables';
import { getDomains } from '@/api/tracker-domains';
import { getCampaigns } from '@/api/tracker-campaigns';
import { formatDateTime } from '@/lib/datetime';

function statusBadge(status: string) {
  const cls = status === 'active' ? 'bg-success-soft text-success' : 'bg-surface-muted text-content-muted';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status}</span>;
}

export function PlayableLinks() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ domain_id: '', slug: '', name: '', notes: '' });
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data: links = [], isLoading } = useQuery({ queryKey: ['playable-links'], queryFn: getPlayableLinks });
  const { data: domains = [] } = useQuery({ queryKey: ['tracker-domains'], queryFn: getDomains });

  const createMut = useMutation({
    mutationFn: createPlayableLink,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['playable-links'] });
      setCreateOpen(false); setForm({ domain_id: '', slug: '', name: '', notes: '' }); setError('');
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: deletePlayableLink,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playable-links'] }),
    onError: (e: Error) => alert(e.message),
  });

  const toggleStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updatePlayableLink(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playable-links'] }),
  });

  const columns = [
    { key: 'name', label: 'Name', render: (l: PlayableLink) => (
      <div>
        <div className="font-medium">{l.name}</div>
        <div className="text-xs text-content-subtle font-mono">https://{l.domain_hostname}/p/{l.slug}</div>
      </div>
    )},
    { key: 'items_count', label: 'A/B items', render: (l: PlayableLink) => <span className="text-sm">{l.items_count ?? 0}</span> },
    { key: 'status', label: 'Status', render: (l: PlayableLink) => statusBadge(l.status) },
    { key: 'created_at', label: 'Created', render: (l: PlayableLink) => <span className="text-xs text-content-subtle">{formatDateTime(l.created_at)}</span> },
    { key: 'actions', label: '', render: (l: PlayableLink) => (
      <div className="flex gap-3 text-xs">
        <button onClick={(e) => { e.stopPropagation(); toggleStatusMut.mutate({ id: l.id, status: l.status === 'active' ? 'paused' : 'active' }); }} className="text-content-muted hover:underline">
          {l.status === 'active' ? 'Pause' : 'Activate'}
        </button>
        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`https://${l.domain_hostname}/p/${l.slug}`); }} className="text-primary hover:underline">Copy URL</button>
        <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete link "${l.name}"?`)) deleteMut.mutate(l.id); }} className="text-danger hover:underline">Delete</button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Playable Links</h1>
          <p className="text-sm text-content-subtle mt-1">Public URLs for ad networks. Each link can A/B-rotate between (playable × campaign) pairs.</p>
        </div>
        <EditOnly><Button onClick={() => { setForm({ domain_id: '', slug: '', name: '', notes: '' }); setError(''); setCreateOpen(true); }}>Create link</Button></EditOnly>
      </div>

      <DataTable
        columns={columns}
        data={links}
        loading={isLoading}
        keyExtractor={(l) => l.id}
        onRowClick={(l) => setDetailId(l.id)}
      />

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Create playable link" size="md">
        <div className="space-y-4">
          {error && <div className="p-3 bg-danger-soft text-sm text-danger rounded">{error}</div>}
          <div>
            <label className="block text-sm font-medium mb-1">Domain</label>
            <select
              value={form.domain_id} onChange={(e) => setForm((f) => ({ ...f, domain_id: e.target.value }))}
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— select —</option>
              {domains.filter((d) => d.status === 'active' && !d.client_id).map((d) => (
                <option key={d.id} value={d.id}>{d.hostname}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Slug</label>
            <input
              type="text" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="my-game-v1"
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-content-subtle">URL: https://&lt;domain&gt;/p/&lt;slug&gt;</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate(form)} loading={createMut.isPending}>Create</Button>
          </div>
        </div>
      </Modal>

      {detailId && <LinkDetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function LinkDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: link, isLoading } = useQuery({
    queryKey: ['playable-link', id],
    queryFn: () => getPlayableLink(id),
  });
  const { data: playables = [] } = useQuery({ queryKey: ['playables'], queryFn: getPlayables });
  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });

  const [newItem, setNewItem] = useState({ playable_id: '', target_campaign_id: '', weight: 1 });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['playable-link', id] });
    qc.invalidateQueries({ queryKey: ['playable-links'] });
  };

  const addMut = useMutation({
    mutationFn: () => addPlayableLinkItem(id, newItem),
    onSuccess: () => { setNewItem({ playable_id: '', target_campaign_id: '', weight: 1 }); invalidate(); },
    onError: (e: Error) => alert(e.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: any }) => updatePlayableLinkItem(id, itemId, body),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (itemId: string) => deletePlayableLinkItem(id, itemId),
    onSuccess: invalidate,
  });

  return (
    <Modal isOpen onClose={onClose} title="Playable link" size="xl">
      {isLoading || !link ? (
        <div className="py-8 text-center text-content-subtle">Loading…</div>
      ) : (
        <LinkDetailBody
          link={link}
          playables={playables}
          campaigns={campaigns}
          newItem={newItem}
          setNewItem={setNewItem}
          onAdd={() => addMut.mutate()}
          onUpdate={(itemId, body) => updateMut.mutate({ itemId, body })}
          onDelete={(itemId) => deleteMut.mutate(itemId)}
          adding={addMut.isPending}
        />
      )}
    </Modal>
  );
}

function LinkDetailBody({
  link, playables, campaigns, newItem, setNewItem, onAdd, onUpdate, onDelete, adding,
}: {
  link: PlayableLinkDetail;
  playables: { id: string; name: string; status: string }[];
  campaigns: { id: string; name: string; slug: string; status: string }[];
  newItem: { playable_id: string; target_campaign_id: string; weight: number };
  setNewItem: (v: { playable_id: string; target_campaign_id: string; weight: number }) => void;
  onAdd: () => void;
  onUpdate: (itemId: string, body: any) => void;
  onDelete: (itemId: string) => void;
  adding: boolean;
}) {
  const url = `https://${link.domain_hostname}/p/${link.slug}`;
  const totalWeight = link.items.filter((i) => i.status === 'active').reduce((s, i) => s + i.weight, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="bg-surface-muted p-3 rounded">
        <div className="text-xs text-content-subtle mb-1">Public URL</div>
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono flex-1 truncate">{url}</code>
          <button onClick={() => navigator.clipboard.writeText(url)} className="text-xs text-primary hover:underline">Copy</button>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">A/B items <span className="text-content-subtle font-normal">({link.items.length})</span></h3>
        <table className="w-full text-sm">
          <thead className="text-xs text-content-subtle uppercase tracking-wide">
            <tr>
              <th className="text-left py-2">Playable</th>
              <th className="text-left py-2">Campaign</th>
              <th className="text-left py-2 w-24">Weight</th>
              <th className="text-left py-2 w-20">%</th>
              <th className="text-left py-2 w-24">Status</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {link.items.map((it) => (
              <tr key={it.id} className="border-t">
                <td className="py-2">{it.playable_name || it.playable_id}</td>
                <td className="py-2">{it.campaign_name || it.campaign_slug || it.target_campaign_id}</td>
                <td className="py-2">
                  <input
                    type="number" min={0} max={1000} defaultValue={it.weight}
                    onBlur={(e) => {
                      const w = parseInt(e.target.value, 10);
                      if (!isNaN(w) && w !== it.weight) onUpdate(it.id, { weight: w });
                    }}
                    className="w-20 px-2 py-1 border border-border rounded text-sm"
                  />
                </td>
                <td className="py-2 text-xs text-content-subtle">
                  {it.status === 'active' ? `${((it.weight / totalWeight) * 100).toFixed(0)}%` : '—'}
                </td>
                <td className="py-2">
                  <button
                    onClick={() => onUpdate(it.id, { status: it.status === 'active' ? 'paused' : 'active' })}
                    className="text-xs text-content-muted hover:underline"
                  >
                    {it.status}
                  </button>
                </td>
                <td className="py-2 text-right">
                  <button onClick={() => onDelete(it.id)} className="text-xs text-danger hover:underline">Remove</button>
                </td>
              </tr>
            ))}
            {link.items.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-content-subtle">No items yet — add one below.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-sm font-semibold mb-2">Add A/B item</h3>
        <div className="grid grid-cols-12 gap-2">
          <select
            value={newItem.playable_id}
            onChange={(e) => setNewItem({ ...newItem, playable_id: e.target.value })}
            className="col-span-5 px-2 py-2 border border-border-strong rounded text-sm"
          >
            <option value="">— playable —</option>
            {playables.filter((p) => p.status === 'active').map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select
            value={newItem.target_campaign_id}
            onChange={(e) => setNewItem({ ...newItem, target_campaign_id: e.target.value })}
            className="col-span-5 px-2 py-2 border border-border-strong rounded text-sm"
          >
            <option value="">— tracker campaign —</option>
            {campaigns.filter((c) => c.status === 'active').map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.slug})</option>
            ))}
          </select>
          <input
            type="number" min={1} max={1000}
            value={newItem.weight}
            onChange={(e) => setNewItem({ ...newItem, weight: parseInt(e.target.value, 10) || 1 })}
            className="col-span-1 px-2 py-2 border border-border-strong rounded text-sm"
          />
          <Button
            onClick={onAdd}
            loading={adding}
            disabled={!newItem.playable_id || !newItem.target_campaign_id}
            className="col-span-1"
          >Add</Button>
        </div>
      </div>
    </div>
  );
}
